/**
 * A client's requests about their own personal data (access, rectification, cancellation, opposition;
 * LFPDPPP arts. 21-34, S-32), and the queue of the person who answers them. The server answers 403 to
 * everyone but the designated compliance officer and super_admins on the staff side, and 404 to a client
 * asking for a request that is not theirs.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export type DataRight = 'access' | 'rectification' | 'cancellation' | 'opposition';
export type DataRequestStatus = 'received' | 'answered' | 'executed' | 'closed';
export type Determination = 'accepted' | 'partial' | 'refused';

export interface DataRequest {
  id: string;
  right: DataRight;
  description: string;
  requested_change: string | null;
  received_at: string;
  answer_due_on: string;
  extended: boolean;
  status: DataRequestStatus;
  answered_at: string | null;
  determination: Determination | null;
  response_text: string | null;
  has_access_copy: boolean;
  execute_due_on: string | null;
  executed_at: string | null;
  blocked_until: string | null;
}

export interface StaffDataRequest extends DataRequest {
  client_id: string;
  client_name: string | null;
  ground: string | null;
  execution_note: string | null;
  answer_overdue: boolean;
  execution_overdue: boolean;
  /** Regulated records the client has (art. 25 fr. I-III): ledger, contracts, kyc_file. */
  retention_holds: string[];
}

export interface NewDataRequest {
  right: DataRight;
  description: string;
  requested_change?: string;
  mfa_code: string;
}

export interface AnswerInput {
  determination: Determination;
  ground: string | null;
  response_text: string;
  include_documents: boolean;
  mfa_code: string;
}

export interface ExecuteInput {
  note: string;
  kyc_case_id: string | null;
  mfa_code: string;
}

const CLIENT = '/api/v1/privacy/requests';
const STAFF = '/api/v1/compliance/data-requests';

class DataRequestsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async mine(): Promise<DataRequest[]> {
    return (await this.api.get<DataRequest[]>(CLIENT)).data;
  }

  async ask(body: NewDataRequest): Promise<DataRequest> {
    return (await this.api.post<DataRequest>(CLIENT, body)).data;
  }

  async accessCopyUrl(id: string, mfaCode: string): Promise<string> {
    const res = await this.api.post<{ url: string }>(`${CLIENT}/${id}/access-copy`, {
      mfa_code: mfaCode,
    });
    return res.data.url;
  }

  async queue(openOnly: boolean): Promise<StaffDataRequest[]> {
    return (await this.api.get<StaffDataRequest[]>(STAFF, { params: { open_only: openOnly } })).data;
  }

  async one(id: string): Promise<StaffDataRequest> {
    return (await this.api.get<StaffDataRequest>(`${STAFF}/${id}`)).data;
  }

  async extend(id: string, note: string): Promise<StaffDataRequest> {
    return (await this.api.post<StaffDataRequest>(`${STAFF}/${id}/extend`, { note })).data;
  }

  async answer(id: string, body: AnswerInput): Promise<StaffDataRequest> {
    return (await this.api.post<StaffDataRequest>(`${STAFF}/${id}/answer`, body)).data;
  }

  async execute(id: string, body: ExecuteInput): Promise<StaffDataRequest> {
    return (await this.api.post<StaffDataRequest>(`${STAFF}/${id}/execute`, body)).data;
  }
}

export default new DataRequestsService();
